// Frontend role helpers.
//
// Post-cutover the source of truth for a user's role is the app_roles table on
// the backend. The FE can't query it per render, so it reads the role from the
// JWT `role` claim (minted from app_roles at login/refresh) surfaced on the
// AppUser object as `user.role`. These helpers gate UI VISIBILITY only — the
// backend re-checks app_roles on every request, so a stale claim can never grant
// real access. A role change therefore takes effect for ACCESS immediately (next
// request), and for NAV/BUTTON visibility on the user's next token refresh
// (≤15 min) or re-login. Acceptable for role changes.
//
// The old email-array checks (isInternalEmail / isSuperAdminEmail / isAdminEmail
// / the @sea-net.co.kr domain rule) are gone: admin is explicit-grant only now,
// no domain implies admin — which also removes the old FE/BE divergence where
// the FE showed admin buttons the backend then 403'd.

export type AppRole = 'execution' | 'design' | 'strategy' | 'super_admin'
export type AppCapability = 'read' | 'download' | 'upload' | 'generate' | 'project_manage' | 'manage'

// anything carrying a role claim — the AppUser, or a decoded JWT payload.
type RoleBearer = { role?: string | null } | null | undefined

export function roleOf(user: RoleBearer): AppRole {
  const r = user?.role
  if (r === 'super_admin' || r === 'strategy' || r === 'design' || r === 'execution') return r
  // Transitional mapping for access tokens minted before the four-role deploy.
  if (r === 'admin') return 'strategy'
  return 'execution'
}

export function hasCapability(user: RoleBearer, capability: AppCapability): boolean {
  const role = roleOf(user)
  if (role === 'super_admin') return true
  if (capability === 'read') return true
  // Project/engineering management is separate from platform administration.
  // Accounts, roles, licenses, audit logs, and releases must use isSuperAdmin.
  if (role === 'strategy') return capability === 'download' || capability === 'upload' || capability === 'generate' || capability === 'project_manage'
  if (role === 'design') return capability === 'download'
  return false
}

// admin OR super — the "internal/admin" capability. Replaces the former
// isInternalEmail AND isAdminEmail helpers (both collapse to admin-or-super).
export function isAdmin(user: RoleBearer): boolean {
  return hasCapability(user, 'upload')
}

export function isSuperAdmin(user: RoleBearer): boolean {
  return roleOf(user) === 'super_admin'
}

// Role management is a super-admin capability, matching the backend
// requireSuperAdmin write gate on /api/app_roles. (Previously restricted to a
// single hardcoded super-super email; now any super_admin — see the Phase 3
// report note.)
export function canManageRoles(user: RoleBearer): boolean {
  return roleOf(user) === 'super_admin'
}

export function canRead(user: RoleBearer): boolean {
  return hasCapability(user, 'read')
}

export function canDownload(user: RoleBearer): boolean {
  return hasCapability(user, 'download')
}

export function canUpload(user: RoleBearer): boolean {
  return hasCapability(user, 'upload')
}

export function canGenerate(user: RoleBearer): boolean {
  return hasCapability(user, 'generate')
}

// Engineering/project lifecycle only; never use this for platform administration.
export function canManage(user: RoleBearer): boolean {
  return hasCapability(user, 'project_manage')
}
